import React, { useEffect, useCallback, useState } from 'react';
import { Modal, ModalBody } from '../common';

export interface FeatureLockData {
  featureName: string;
  featureDescription: string;
  featureIcon?: string;
  lockType: 'subscription' | 'achievement';
  requirementText?: string;
  achievementUrl?: string;
}

interface FeatureLockModalProps {
  /** URL to subscription page */
  subscriptionUrl: string;
  /** Translations for modal content */
  translations: {
    featureLocked: string;
    featureLockedDescription: string;
    requirement: string;
    subscribe: string;
    seeAchievement: string;
    maybeLater: string;
  };
}

// Global function to show the modal - will be set when the component mounts
let showModalFunction: ((data: FeatureLockData) => void) | null = null;

/**
 * Shows the feature lock modal with the specified data
 * This function can be called from outside React components
 */
export function showFeatureLockModal(data: FeatureLockData): void {
  if (showModalFunction) {
    showModalFunction(data);
  }
}

/**
 * FeatureLockModal - Shows when a user tries to access a locked feature
 *
 * Ported from:
 * - app/views/diaries/shared/_feature_lock_modal.html.erb
 * - app/javascript/controllers/feature_lock_modal_controller.js
 *
 * This modal is shown imperatively when a user clicks on a locked feature.
 * Use the exported `showFeatureLockModal` function to display it.
 */
export function FeatureLockModal({ subscriptionUrl, translations }: FeatureLockModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<FeatureLockData | null>(null);

  // Register the global show function
  useEffect(() => {
    showModalFunction = (data: FeatureLockData) => {
      setModalData(data);
      setIsOpen(true);
    };

    return () => {
      showModalFunction = null;
    };
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const featureName = modalData?.featureName || translations.featureLocked;
  const featureDescription = modalData?.featureDescription || translations.featureLockedDescription;
  const featureIcon = modalData?.featureIcon || 'lock-fill';
  const lockType = modalData?.lockType || 'subscription';
  const requirementText = modalData?.requirementText;
  const achievementUrl = modalData?.achievementUrl;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={featureName}
      centered
    >
      <ModalBody className="text-center p-5">
        {/* Lock Icon */}
        <div className="mb-4">
          <div className="d-inline-block position-relative">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center bg-secondary bg-opacity-10 mx-auto"
              style={{ width: 100, height: 100 }}
            >
              <i className={`bi bi-${featureIcon} text-secondary fs-1`}></i>
            </div>
          </div>
        </div>

        {/* Feature Name */}
        <h3 className="mb-3 fw-bold">{featureName}</h3>

        {/* Feature Description */}
        <p className="text-muted mb-4">{featureDescription}</p>

        {/* Requirement Section */}
        {requirementText && (
          <div className="alert alert-info mb-4">
            <div className="d-flex align-items-start">
              <i className="bi bi-info-circle-fill me-2 mt-1"></i>
              <div className="text-start">
                <strong>{translations.requirement}:</strong>
                <div>{requirementText}</div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-grid gap-2">
          {/* Subscribe Button (for subscription features) */}
          {lockType === 'subscription' && (
            <a href={subscriptionUrl} className="btn btn-primary btn-lg" onClick={handleClose}>
              <i className="bi bi-star-fill me-2"></i>
              {translations.subscribe}
            </a>
          )}

          {/* See Achievement Button (for achievement features) */}
          {lockType === 'achievement' && achievementUrl && (
            <a href={achievementUrl} className="btn btn-primary btn-lg" onClick={handleClose}>
              <i className="bi bi-trophy me-2"></i>
              {translations.seeAchievement}
            </a>
          )}

          {/* Close Button */}
          <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>
            {translations.maybeLater}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}
